import { createContext, useCallback, useContext, useMemo } from 'react';
import {
  PACKAGE_TOPHIGHLIGHTS,
  YEARLY_TERM,
} from 'components/PricingPackaging/constants';
import { useOrgPricing } from '../components/PricingPackaging/SelectPlan';
import {
  getAllSelectedBundlePrices,
  getAnnualSavings,
} from '../components/PricingPackaging/utils';

export const PricingPackagingContext = createContext({});

export const usePricingPackagingContext = () =>
  useContext(PricingPackagingContext);

const PricingPackagingContextProvider = ({
  canChoosePlan = false,
  children,
  commonBundleCardProps = {},
  currentPlan,
  displayPrice = true,
  isMobileView,
  onBundleCardCtaOnClick,
  onToggleFeatureList,
  priceTerm = YEARLY_TERM,
  topHighlightsPackages = PACKAGE_TOPHIGHLIGHTS,
}) => {
  const {
    loading: loadingPrices,
    monthlyPackagePrices,
    yearlyPackagePrices,
  } = useOrgPricing() || {};

  const isAnnual = useMemo(() => priceTerm === YEARLY_TERM, [priceTerm]);

  const getBundlePackagePrices = useCallback(
    (bundleName) =>
      getAllSelectedBundlePrices({
        bundleName,
        monthlyPackagePrices,
        yearlyPackagePrices,
      }),
    [monthlyPackagePrices, yearlyPackagePrices]
  );

  const getBundlePrices = useCallback(
    (bundleName) => {
      const { monthlyPrices = {}, yearlyPrices = {} } =
        getBundlePackagePrices(bundleName);

      return isAnnual ? yearlyPrices : monthlyPrices;
    },
    [isAnnual, getBundlePackagePrices]
  );

  const getPrice = useCallback(
    (bundleName) => {
      const bundlePrices = getBundlePrices(bundleName);
      return bundlePrices?.price;
    },
    [getBundlePrices]
  );

  const getSavings = useCallback(
    (bundleName) => {
      const { monthlyPrices = {}, yearlyPrices = {} } =
        getBundlePackagePrices(bundleName);

      return getAnnualSavings({ annualPrices: yearlyPrices, monthlyPrices });
    },
    [getBundlePackagePrices]
  );

  const pricingPackagingData = useMemo(
    () => ({
      canChoosePlan,
      commonBundleCardProps,
      currentPlan,
      displayPrice,
      getBundlePrices,
      getPrice,
      getSavings,
      isAnnual,
      isMobileView,
      loadingPrices,
      monthlyPackagePrices,
      onBundleCardCtaOnClick,
      onToggleFeatureList,
      priceTerm,
      topHighlightsPackages,
      yearlyPackagePrices,
    }),
    [
      canChoosePlan,
      commonBundleCardProps,
      currentPlan,
      displayPrice,
      getBundlePrices,
      getPrice,
      getSavings,
      isAnnual,
      isMobileView,
      loadingPrices,
      monthlyPackagePrices,
      onBundleCardCtaOnClick,
      onToggleFeatureList,
      priceTerm,
      topHighlightsPackages,
      yearlyPackagePrices,
    ]
  );

  return (
    <PricingPackagingContext.Provider value={pricingPackagingData}>
      {children}
    </PricingPackagingContext.Provider>
  );
};

export default PricingPackagingContextProvider;
